import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import type {
	SubscriptionCheckoutDTO,
	SubscriptionDirectDTO,
	SubscriptionPendingDTO,
} from '@libs/dtos/order/order-request.dto';
import type { SubscriptionListResponseDTO, SubscriptionResponseDTO } from '@libs/dtos/subscription/subscription.dto';
import { Observable } from 'rxjs';

export interface CreateSubscriptionCheckoutResponse {
	checkoutUrl: string;
	subscriptionId: number;
}

export interface CreateSubscriptionPendingResponse {
	success: boolean;
	subscriptionId: number;
	/** Nothing to pay (0 €): the subscription was created paid and active, no payment instructions */
	paid?: boolean;
}

export type SubscriptionResponse = SubscriptionResponseDTO;

@Injectable({
	providedIn: 'root',
})
export class SubscriptionService {
	private readonly baseUrl = `${environment.pathBackend}/subscriptions`;

	constructor(private http: HttpClient) {}

	createCheckout(data: SubscriptionCheckoutDTO): Observable<CreateSubscriptionCheckoutResponse> {
		return this.http.post<CreateSubscriptionCheckoutResponse>(`${this.baseUrl}/checkout`, data);
	}

	createPending(data: SubscriptionPendingDTO): Observable<CreateSubscriptionPendingResponse> {
		return this.http.post<CreateSubscriptionPendingResponse>(`${this.baseUrl}/pending`, data);
	}

	createDirect(data: SubscriptionDirectDTO): Observable<CreateSubscriptionPendingResponse> {
		return this.http.post<CreateSubscriptionPendingResponse>(`${this.baseUrl}/direct`, data);
	}

	getSubscriptions(params?: Record<string, string>): Observable<SubscriptionListResponseDTO> {
		return this.http.get<SubscriptionListResponseDTO>(this.baseUrl, { params });
	}

	getSubscription(id: number): Observable<SubscriptionResponse> {
		return this.http.get<SubscriptionResponse>(`${this.baseUrl}/${id}`);
	}

	pause(id: number): Observable<SubscriptionResponse> {
		return this.http.post<SubscriptionResponse>(`${this.baseUrl}/${id}/pause`, {});
	}

	resume(id: number): Observable<SubscriptionResponse> {
		return this.http.post<SubscriptionResponse>(`${this.baseUrl}/${id}/resume`, {});
	}

	cancel(id: number): Observable<SubscriptionResponse> {
		return this.http.post<SubscriptionResponse>(`${this.baseUrl}/${id}/cancel`, {});
	}

	/** Admin: the Revolut/Bizum payment arrived, the subscription starts */
	approvePayment(id: number): Observable<SubscriptionResponse> {
		return this.http.post<SubscriptionResponse>(`${this.baseUrl}/${id}/approve-payment`, {});
	}

	/** Admin: the payment never arrived, the subscription is cancelled */
	rejectPayment(id: number): Observable<SubscriptionResponse> {
		return this.http.post<SubscriptionResponse>(`${this.baseUrl}/${id}/reject-payment`, {});
	}

	confirmPayment(id: number): Observable<SubscriptionResponse> {
		return this.http.post<SubscriptionResponse>(`${this.baseUrl}/${id}/confirm-payment`, {});
	}

	redirectToCheckout(checkoutUrl: string): void {
		window.location.href = checkoutUrl;
	}
}
